import React, { useCallback, useEffect, useState } from "react";
import {
    listCategoryRequests,
    setCategoryRequestStatus,
    CategoryRequestDTO,
    CategoryRequestStatus,
} from "../../api/CategoryRequestAPI";
import { getUserMessage, logApiError } from "../../api/apiError";

interface Props {
    token: string;
    /** Start creating a category pre-filled with the requested name. */
    onCreateCategory?: (name: string, text?: string) => void;
}

/** Admin queue of category requests: review, create the category, mark done. */
const CategoryRequestAdminList: React.FC<Props> = ({ token, onCreateCategory }) => {
    const [status, setStatus] = useState<CategoryRequestStatus>("OPEN");
    const [requests, setRequests] = useState<CategoryRequestDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setRequests(await listCategoryRequests(token, status));
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
        } finally {
            setLoading(false);
        }
    }, [token, status]);

    useEffect(() => { load(); }, [load]);

    const markStatus = async (r: CategoryRequestDTO, next: CategoryRequestStatus) => {
        if (!r.id || r.version == null) return;
        setError(null);
        try {
            await setCategoryRequestStatus(token, r.id, r.version, next);
            setRequests((prev) => prev.filter((x) => x.id !== r.id));
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
            await load(); // refresh versions after a conflict
        }
    };

    return (
        <div style={{ marginTop: "1rem", border: "1px solid var(--border-color)", borderRadius: "var(--radius-md, 6px)", padding: "0.75rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.5rem" }}>
                <strong>Category requests</strong>
                <select value={status} onChange={(e) => setStatus(e.target.value as CategoryRequestStatus)}>
                    <option value="OPEN">Open</option>
                    <option value="DONE">Done</option>
                </select>
                <button type="button" className="btn-sm btn-secondary" onClick={load} disabled={loading}>
                    {loading ? "Loading…" : "Refresh"}
                </button>
            </div>

            {error && <div className="error-message" style={{ marginBottom: "0.5rem" }}>{error}</div>}

            {requests.length === 0 && !loading ? (
                <div style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>
                    No {status.toLowerCase()} requests.
                </div>
            ) : (
                <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    {requests.map((r) => (
                        <li key={r.id} style={{ border: "1px solid var(--border-color)", borderRadius: "4px", padding: "0.5rem" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                                <strong>{r.name}</strong>
                                {r.createdAt && (
                                    <span style={{ fontSize: "0.78rem", color: "var(--text-secondary)" }}>
                                        {new Date(r.createdAt).toLocaleDateString()}
                                    </span>
                                )}
                                <span style={{ marginLeft: "auto", display: "flex", gap: "0.4rem" }}>
                                    {onCreateCategory && status === "OPEN" && (
                                        <button type="button" className="btn-sm btn-secondary" onClick={() => onCreateCategory(r.name, r.text)}>
                                            Create category
                                        </button>
                                    )}
                                    {status === "OPEN" ? (
                                        <button type="button" className="btn-sm btn-success" onClick={() => markStatus(r, "DONE")}>
                                            Mark done
                                        </button>
                                    ) : (
                                        <button type="button" className="btn-sm btn-secondary" onClick={() => markStatus(r, "OPEN")}>
                                            Reopen
                                        </button>
                                    )}
                                </span>
                            </div>
                            {r.text && (
                                <div style={{ whiteSpace: "pre-wrap", marginTop: "0.35rem", fontSize: "0.9rem" }}>{r.text}</div>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default CategoryRequestAdminList;